import React, {useMemo} from 'react'
import {
    useGetConfigsByKeysQuery,
    useCreateAutoInvoiceMutation,
    useSyncZeusintDailyMutation,
} from 'store/Store'
import {
    Box,
    CircularProgress,
    Typography,
    Divider,
    List,
    ListItem,
    Chip,
} from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import dayjs from 'dayjs'
import {Colors} from 'appearance'
import Button from 'components/Control/Button'
import {toast} from 'sonner'
import {useTranslation} from 'react-i18next'

const CONFIG_KEYS = [
    'cronCreateInvoices',
    'cronMeterReadingError',
    'cronZeusintReadings',
    'cronCreateInvoicesPreview',
]

const InfoSettings = () => {
    const {t} = useTranslation()
    const {data, isLoading, refetch} = useGetConfigsByKeysQuery({keys: CONFIG_KEYS})
    const [doCreateAutoInvoice] = useCreateAutoInvoiceMutation()
    const [doSyncZeusintDaily] = useSyncZeusintDailyMutation()

    const configs = useMemo(() => {
        const items = Array.isArray(data) ? data : []
        const byKey = new Map(items.map((item) => [item?.key, item]))
        return CONFIG_KEYS.map((key) => byKey.get(key) || {key, value: null})
    }, [data])

    const onCreateAutoInvoice = () =>
        toast.promise(
            doCreateAutoInvoice({}).unwrap().then((res) => { refetch(); return res }),
            {
                loading: t('loading'),
                success: () => t('invoices.submit.create.success'),
                error: (err) => err?.data?.error || t('invoices.submit.create.error'),
            }
        )

    const onSyncZeusintDaily = () =>
        toast.promise(
            doSyncZeusintDaily().unwrap().then((res) => { refetch(); return res }),
            {
                loading: t('loading'),
                success: () => t('meters.syncZeusintDailySuccess'),
                error: (err) => err?.data?.error || t('meters.syncZeusintDailyError'),
            }
        )

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <List disablePadding>
            {configs.map((item, index) => {
                const {key, value} = item
                const {status, date} = value || {}
                const normalizedStatus = (status || '').trim().toLowerCase()
                const isSuccess = normalizedStatus === 'success'
                const isError = normalizedStatus && !isSuccess

                const dateLabel =
                    date && dayjs(date).isValid()
                        ? dayjs(date).format('DD/MM/YYYY HH:mm')
                        : t('general.notAvailable')

                let StatusIcon = RadioButtonUncheckedRoundedIcon
                if (isSuccess) StatusIcon = CheckCircleRoundedIcon
                else if (isError) StatusIcon = CancelRoundedIcon

                let iconColor = Colors.APP_FADED_GREY
                if (isSuccess) iconColor = Colors.success
                else if (isError) iconColor = Colors.danger

                let chipColor = 'default'
                if (isSuccess) chipColor = 'success'
                else if (isError) chipColor = 'error'

                let chipLabel = t('general.notAvailable')
                if (isSuccess) chipLabel = 'success'
                else if (isError) chipLabel = normalizedStatus

                let action = null
                if (key === 'cronCreateInvoices') {
                    action = (
                        <Button variant="outlined" size="small" onClick={onCreateAutoInvoice}>
                            {t('invoices.createInvoice')}
                        </Button>
                    )
                } else if (key === 'cronZeusintReadings') {
                    action = (
                        <Button variant="outlined" size="small" onClick={onSyncZeusintDaily}>
                            {t('meters.syncZeusintDaily')}
                        </Button>
                    )
                }

                return (
                    <React.Fragment key={key}>
                        <ListItem
                            disableGutters
                            sx={{py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5}}>
                            {/* Status icon */}
                            <StatusIcon sx={{color: iconColor, fontSize: 20, flexShrink: 0}} />

                            {/* Key + date */}
                            <Box flex={1} minWidth={0}>
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                    title={key}>
                                    {key}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {dateLabel}
                                </Typography>
                            </Box>

                            {/* Status chip */}
                            <Chip
                                label={chipLabel}
                                color={chipColor}
                                size="small"
                                variant="outlined"
                                sx={{textTransform: 'capitalize', flexShrink: 0}}
                            />

                            {/* Action button */}
                            {action}
                        </ListItem>
                        {index < configs.length - 1 && <Divider />}
                    </React.Fragment>
                )
            })}
        </List>
    )
}

export default InfoSettings
